import { BOAT_TYPES } from './boatTypes';

/**
 * Utility functions for validating rigging configuration against rower side preferences
 */

/**
 * Parse rigging configuration from JSON string
 * @param {string} riggingConfig - JSON string of rigging configuration
 * @returns {Object} Parsed rigging configuration or empty object
 */
export const parseRiggingConfig = (riggingConfig) => {
  if (!riggingConfig) return {};

  try {
    // Handle case where riggingConfig might already be an object
    if (typeof riggingConfig === 'object') {
      return riggingConfig;
    }

    // Handle case where riggingConfig is a string
    if (typeof riggingConfig === 'string') {
      // Check if it's an empty string or just whitespace
      if (
        riggingConfig.trim() === '' ||
        riggingConfig.trim() === '{}' ||
        riggingConfig.trim() === 'null'
      ) {
        return {};
      }
      return JSON.parse(riggingConfig);
    }

    console.warn('Unexpected rigging config type:', typeof riggingConfig, riggingConfig);
    return {};
  } catch (error) {
    console.error('Failed to parse rigging config:', error, 'Raw value:', riggingConfig);
    return {};
  }
};

/**
 * Get the expected side for a specific seat based on rigging configuration
 * @param {Object} riggingConfig - Parsed rigging configuration object
 * @param {number} seatNumber - Seat number (1-based for rowers, 0 for cox)
 * @returns {string|null} Expected side ('port', 'starboard', or null if not configured)
 */
export const getExpectedSideForSeat = (riggingConfig, seatNumber) => {
  if (!riggingConfig || typeof seatNumber !== 'number') return null;

  // Cox position (seat 0) is not part of rigging configuration
  if (seatNumber === 0) return null;

  // Try both number and string keys since rigging config might use string keys
  return riggingConfig[seatNumber] || riggingConfig[seatNumber.toString()] || null;
};

/**
 * Validate if a rower's preferred side matches the expected side for a seat
 * @param {Object} rower - Rower object with preferred_side property
 * @param {string} expectedSide - Expected side from rigging config ('port', 'starboard', or null)
 * @returns {Object} Validation result with isValid and message
 */
export const validateRowerSideForSeat = (rower, expectedSide) => {
  if (!expectedSide) {
    return { isValid: true, message: null };
  }

  const rowerPreferredSide = rower.preferred_side || rower.side_preference;

  if (!rowerPreferredSide || rowerPreferredSide === 'none' || rowerPreferredSide === 'both') {
    return {
      isValid: true,
      message: `Rower has no side preference - can be assigned to ${expectedSide} side`,
    };
  }

  if (rowerPreferredSide === expectedSide) {
    return {
      isValid: true,
      message: `Perfect match: ${rowerPreferredSide} rower on ${expectedSide} side`,
    };
  }

  return {
    isValid: false,
    message: `Side mismatch: ${rowerPreferredSide} rower assigned to ${expectedSide} side`,
  };
};

/**
 * Get validation warnings for a crew lineup based on rigging configuration
 * @param {Object} crew - Crew object with rigging_config and side preference info
 * @param {Array} lineup - Array of rowers in lineup positions
 * @param {Object} options - Optional configuration
 * @param {boolean} options.skipExperimentalCrews - Whether to skip validation for experimental crews (default: false)
 * @param {boolean} options.enhanceWarningMessages - Whether to enhance warning messages with guidance (default: false)
 * @returns {Array} Array of warning messages
 */
export const getRiggingValidationWarnings = (crew, lineup, options = {}) => {
  const { skipExperimentalCrews = false, enhanceWarningMessages = false } = options;
  const warnings = [];

  // Skip validation for experimental crews if requested
  if (skipExperimentalCrews && crew.is_experimental) {
    return warnings;
  }

  // Only validate if crew has strict side preference OR has rigging config
  if (!crew.side_preference_strict && !crew.rigging_config) {
    return warnings;
  }

  const riggingConfig = parseRiggingConfig(crew.rigging_config);

  // Get boat type info to determine seat numbering
  const boatType = BOAT_TYPES[crew.boat_type];
  const hasCox = boatType?.hasCox || false;

  console.log('Rigging validation debug:', {
    crewId: crew.id,
    boatType: crew.boat_type,
    hasCox,
    riggingConfigRaw: crew.rigging_config,
    riggingConfigParsed: riggingConfig,
    riggingConfigKeys: Object.keys(riggingConfig),
    lineupLength: lineup.length,
  });

  // If crew has strict side preference but no rigging config, show error
  if (crew.side_preference_strict && (!riggingConfig || Object.keys(riggingConfig).length === 0)) {
    warnings.push({
      seatNumber: null,
      rowerName: 'Crew Configuration',
      message:
        'Rigging configuration is required for sweep boats. Please set the rigging configuration in crew settings.',
      severity: 'error',
    });
    return warnings;
  }

  if (!riggingConfig || Object.keys(riggingConfig).length === 0) {
    return warnings;
  }

  lineup.forEach((rower, index) => {
    if (!rower) return; // Skip empty positions

    // Convert array index to actual seat number
    // For boats without cox: array index 0 = seat 1, array index 1 = seat 2, etc.
    // For boats with cox: array index 0 = cox (seat 0), array index 1 = seat 1, etc.
    const seatNumber = hasCox ? index : index + 1;

    const expectedSide = getExpectedSideForSeat(riggingConfig, seatNumber);
    if (!expectedSide) return; // Skip if no expected side for this seat

    const validation = validateRowerSideForSeat(rower, expectedSide);
    if (!validation.isValid) {
      console.log('Rigging validation warning:', {
        arrayIndex: index,
        seatNumber,
        hasCox,
        rowerName: `${rower.first_name} ${rower.last_name}`,
        rowerPreferredSide: rower.preferred_side || rower.side_preference,
        expectedSide,
        message: validation.message,
      });

      // Enhance warning messages if requested
      let warningMessage = validation.message;
      if (enhanceWarningMessages) {
        const rowerPreferredSide = rower.preferred_side || rower.side_preference;
        if (rowerPreferredSide && rowerPreferredSide !== 'none' && rowerPreferredSide !== 'both') {
          warningMessage = `${validation.message}. Consider updating ${rower.first_name}'s preferred side in their profile settings.`;
        }
      }

      warnings.push({
        seatNumber,
        rowerName: `${rower.first_name} ${rower.last_name}`,
        message: warningMessage,
        severity: 'warning',
      });
    }
  });

  return warnings;
};

/**
 * Check if a rower can be assigned to a specific seat based on rigging configuration
 * @param {Object} crew - Crew object with rigging_config
 * @param {Object} rower - Rower object with preferred_side
 * @param {number} seatNumber - Target seat number
 * @param {Object} options - Optional configuration
 * @param {boolean} options.skipExperimentalCrews - Whether to skip validation for experimental crews (default: false)
 * @returns {Object} Validation result with canAssign and message
 */
export const canAssignRowerToSeat = (crew, rower, seatNumber, options = {}) => {
  const { skipExperimentalCrews = false } = options;

  // Skip validation for experimental crews if requested
  if (skipExperimentalCrews && crew.is_experimental) {
    return { canAssign: true, message: null };
  }

  // Skip validation for non-strict side preference crews
  if (!crew.side_preference_strict) {
    return { canAssign: true, message: null };
  }

  const riggingConfig = parseRiggingConfig(crew.rigging_config);

  // If crew has strict side preference but no rigging config, show error
  if (!riggingConfig || Object.keys(riggingConfig).length === 0) {
    return {
      canAssign: false,
      message:
        'Rigging configuration is required for sweep boats. Please set the rigging configuration in crew settings.',
    };
  }

  const expectedSide = getExpectedSideForSeat(riggingConfig, seatNumber);

  if (!expectedSide) {
    return { canAssign: true, message: null };
  }

  const validation = validateRowerSideForSeat(rower, expectedSide);

  return {
    canAssign: validation.isValid,
    message: validation.message,
  };
};
